import React, { useState, useEffect, useRef } from 'react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface Season {
  id: number
  name: string
  startDate: string
  endDate: string
  modifierType: string
  modifierValue: number
  priority: number
}

export interface SeasonFormDefaults {
  name: string
  startDate: string
  endDate: string
  modifierType: string
  modifierValue: number
  priority: number
}

interface SeasonFormProps {
  /** The property's backend-served canonical path. */
  propertyBasePath: string
  season?: Season | null
  defaults?: SeasonFormDefaults | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function SeasonForm({ propertyBasePath, season, defaults, open, onOpenChange }: SeasonFormProps) {
  const isEditing = !!season

  const [name, setName] = useState(season?.name ?? defaults?.name ?? '')
  const [startDate, setStartDate] = useState(season?.startDate ?? defaults?.startDate ?? '')
  const [endDate, setEndDate] = useState(season?.endDate ?? defaults?.endDate ?? '')
  const [modifierType, setModifierType] = useState(season?.modifierType ?? defaults?.modifierType ?? 'percentage')
  const [modifierValue, setModifierValue] = useState(
    season?.modifierValue != null ? String(season.modifierValue) : defaults?.modifierValue != null ? String(defaults.modifierValue) : '0'
  )
  const [priority, setPriority] = useState(
    season?.priority != null ? String(season.priority) : defaults?.priority != null ? String(defaults.priority) : '0'
  )
  const [saving, setSaving] = useState(false)
  const review = usePricingReview()
  // "Back" from the review reopens this form with the draft the operator typed, so the reopening
  // must not reset it to the season or the template it started from.
  const resumeDraft = useRef(false)

  // Reset form state when season or defaults change (e.g., template selected, edit clicked)
  useEffect(() => {
    if (open && resumeDraft.current) {
      resumeDraft.current = false
      return
    }
    if (open) {
      setName(season?.name ?? defaults?.name ?? '')
      setStartDate(season?.startDate ?? defaults?.startDate ?? '')
      setEndDate(season?.endDate ?? defaults?.endDate ?? '')
      setModifierType(season?.modifierType ?? defaults?.modifierType ?? 'percentage')
      setModifierValue(
        season?.modifierValue != null ? String(season.modifierValue) : defaults?.modifierValue != null ? String(defaults.modifierValue) : '0'
      )
      setPriority(
        season?.priority != null ? String(season.priority) : defaults?.priority != null ? String(defaults.priority) : '0'
      )
    }
  }, [open, season, defaults])

  // The save goes through the server review first (change safety R1.5). The form steps aside for it,
  // and "Back" brings it back with the draft intact (§5.17.1).
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const data = {
      season: {
        name,
        start_date: startDate,
        end_date: endDate,
        modifier_type: modifierType,
        modifier_value: modifierValue,
        priority,
      },
    }

    const editing = isEditing && season ? season : null
    const reviewBody = editing ? { operation: 'update', id: editing.id, ...data } : { operation: 'create', ...data }
    const callbacks = { onStart: () => setSaving(true), onFinish: () => setSaving(false) }

    onOpenChange(false)
    // Literal suffixes on the served basePath, so the review convention test can read every target.
    review.open(
      `${propertyBasePath}/seasons/review`,
      reviewBody,
      () =>
        editing
          ? inertiaSave('patch', `${propertyBasePath}/seasons/${editing.id}`, data, callbacks)
          : inertiaSave('post', `${propertyBasePath}/seasons`, data, callbacks),
      {
        onBack: () => {
          resumeDraft.current = true
          onOpenChange(true)
        },
      }
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit Season' : 'Add Season'}</DialogTitle>
            <DialogDescription>
              {isEditing
                ? 'Update the seasonal pricing adjustment.'
                : defaults
                  ? 'Review and adjust template values, then save.'
                  : 'Create a new seasonal pricing adjustment.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <DialogBody className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="season-name">Name</Label>
                <Input
                  id="season-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Peak Summer"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="season-start">Start Date</Label>
                  <Input
                    id="season-start"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="season-end">End Date</Label>
                  <Input
                    id="season-end"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="season-modifier-type">Modifier Type</Label>
                  <Select value={modifierType} onValueChange={setModifierType}>
                    <SelectTrigger id="season-modifier-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage (%)</SelectItem>
                      <SelectItem value="absolute">Absolute (GBP)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="season-modifier-value">Modifier Value</Label>
                  <Input
                    id="season-modifier-value"
                    type="number"
                    value={modifierValue}
                    onChange={(e) => setModifierValue(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="season-priority">Priority</Label>
                <Input
                  id="season-priority"
                  type="number"
                  min="0"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Higher priority seasons take precedence when dates overlap.
                </p>
              </div>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-lg" disabled={saving}>
                {saving ? 'Saving...' : isEditing ? 'Update Season' : 'Create Season'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {review.dialog}
    </>
  )
}
